import { useEffect } from 'react'

/**
 * Warns the person before they close or reload the tab, while `active` is
 * true — used by `BackgroundUploadIndicator` so an in-flight interview-video
 * upload (which has no server-side resume: closing the tab loses it, an
 * explicit owner-accepted limit) is never lost silently.
 *
 * The listener is attached/detached on every `active` change rather than
 * once at mount, so it is present ONLY while there is something to lose —
 * once every background upload settles (done or error), the tab can be
 * closed freely again with no leftover listener blocking it.
 */
export function useBeforeUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Chrome ignores `preventDefault()` alone and still requires the legacy
      // `returnValue` assignment to show its native confirmation dialog.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])
}
